import React, { useState, useEffect, useMemo } from 'react';
import { get, post } from '../../Services/http';

const fmt = (amount) => amount.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const signedAmount = (action, amount) => (action === 'Credit' ? -amount : amount);
const fmtDate = (dateStr) => (dateStr ? dateStr.substring(0, 10) : '');

const QliroReport = ({ brand, batchId, batchLabel, backUrl }) => {
    const [settlements, setSettlements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [setPayDate, setSetPayDate] = useState('');
    const [processing, setProcessing] = useState(false);
    const [processedCount, setProcessedCount] = useState(0);
    const [results, setResults] = useState({});
    const [selected, setSelected] = useState({});

    useEffect(() => {
        get(`/api/qliro-report/settlements?brand=${encodeURIComponent(brand)}&batchId=${encodeURIComponent(batchId)}`)
            .then((res) => res.json())
            .then((data) => {
                setSettlements(data);
                const allSelected = {};
                data.forEach((s) => { allSelected[s.paymentTransactionId] = true; });
                setSelected(allSelected);
                setLoading(false);
            })
            .catch((err) => {
                setFetchError(err.response ? `HTTP ${err.response.status}` : err.message);
                setLoading(false);
            });
    }, [brand, batchId]);

    const selectedSettlements = useMemo(
        () => settlements.filter((s) => selected[s.paymentTransactionId]),
        [settlements, selected]
    );

    const allChecked = settlements.length > 0 && selectedSettlements.length === settlements.length;
    const someChecked = selectedSettlements.length > 0 && !allChecked;

    const toggleAll = () => {
        const next = !allChecked;
        const updated = {};
        settlements.forEach((s) => { updated[s.paymentTransactionId] = next; });
        setSelected(updated);
    };

    const toggleRow = (key) => {
        setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleProcess = async () => {
        if (!setPayDate) {
            alert('Ange betaldatum innan du klarmarkerar.');
            return;
        }
        if (!window.confirm(`Klarmarkera ${selectedSettlements.length} markerade fakturor i Fortnox?`)) return;

        setProcessing(true);
        setProcessedCount(0);

        for (const settlement of selectedSettlements) {
            const key = settlement.paymentTransactionId;
            setResults((prev) => ({ ...prev, [key]: { pending: true } }));

            try {
                const res = await post('/api/qliro-report/process-settlement', { brand, setPayDate, settlement });
                const data = await res.json();
                setResults((prev) => ({ ...prev, [key]: data }));
            } catch (err) {
                // The http service throws for non-2xx responses, the body of those can still hold the result
                const data = await err.response?.json().catch(() => null);
                setResults((prev) => ({ ...prev, [key]: data || { errorMessage: err.message } }));
            }

            setProcessedCount((c) => c + 1);
        }

        setProcessing(false);
    };

    const totalAmount = settlements.reduce((sum, s) => sum + signedAmount(s.action, s.amount), 0);
    const totalExVat = settlements.reduce((sum, s) => sum + signedAmount(s.action, s.amountExcludingVat), 0);

    return (
        <div className="simple-table order-summary" style={{ marginTop: '2rem' }}>
            <div className="row medium-unstack no-margin simple-table__header">
                <div className="columns small-12">
                    <h3>Settlements &mdash; Batch {batchLabel || batchId}</h3>
                </div>
            </div>

            <div className="row medium-unstack no-margin simple-table__header" style={{ alignItems: 'center' }}>
                <div className="columns small-12 medium-shrink">
                    <a href={backUrl} className="button secondary" style={{ marginBottom: 0 }}>
                        &#8592; Tillbaka till batcher
                    </a>
                </div>
                <div className="columns small-12 medium-expand">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <label htmlFor="setPayDate" style={{ margin: 0, whiteSpace: 'nowrap' }}>Betaldatum</label>
                        <input
                            type="date"
                            id="setPayDate"
                            value={setPayDate}
                            onChange={(e) => setSetPayDate(e.target.value)}
                            style={{ width: 'auto', margin: 0 }}
                            disabled={processing}
                        />
                        <button
                            className="button alert"
                            style={{ marginBottom: 0, whiteSpace: 'nowrap' }}
                            onClick={handleProcess}
                            disabled={processing || loading || selectedSettlements.length === 0}
                        >
                            {processing
                                ? `Klarmarkerar... (${processedCount}/${selectedSettlements.length})`
                                : `Klarmarkera ${selectedSettlements.length} markerade fakturor i Fortnox`}
                        </button>
                    </div>
                </div>
            </div>

            {loading && (
                <div className="row no-margin">
                    <div className="columns small-12" style={{ padding: '1rem' }}>
                        Laddar settlements...
                    </div>
                </div>
            )}

            {fetchError && (
                <div className="row no-margin">
                    <div className="columns small-12" style={{ padding: '1rem', color: 'red' }}>
                        Fel vid hämtning: {fetchError}
                    </div>
                </div>
            )}

            {!loading && !fetchError && settlements.length === 0 && (
                <div className="row no-margin">
                    <div className="columns small-12" style={{ padding: '1rem' }}>
                        Inga settlements hittades för denna batch.
                    </div>
                </div>
            )}

            {!loading && !fetchError && settlements.length > 0 && (
                <>
                    <div className="row medium-unstack no-margin simple-table__header hide-for-small-only">
                        <div className="columns medium-shrink">
                            <input
                                type="checkbox"
                                checked={allChecked}
                                ref={(el) => { if (el) el.indeterminate = someChecked; }}
                                onChange={toggleAll}
                                disabled={processing}
                                style={{ margin: 0 }}
                            />
                        </div>
                        <div className="columns medium-1">Ordernr</div>
                        <div className="columns medium-2">Litium-order</div>
                        <div className="columns medium-1">Typ</div>
                        <div className="columns medium-1">Valuta</div>
                        <div className="columns medium-1 text--right">Belopp</div>
                        <div className="columns medium-1 text--right">Exkl. moms</div>
                        <div className="columns medium-2">Betaldatum</div>
                        <div className="columns medium-2">Info</div>
                    </div>

                    {settlements.map((s) => {
                        const key = s.paymentTransactionId;
                        const result = results[key];
                        return (
                            <div key={key} className="row medium-unstack no-margin simple-table__body">
                                <div className="columns medium-shrink">
                                    <input
                                        type="checkbox"
                                        checked={!!selected[key]}
                                        onChange={() => toggleRow(key)}
                                        disabled={processing}
                                        style={{ margin: 0 }}
                                    />
                                </div>
                                <div className="columns medium-1">{s.orderId}</div>
                                <div className="columns medium-2">{s.merchantOrderReference}</div>
                                <div className="columns medium-1">{s.action}</div>
                                <div className="columns medium-1">{s.currencyCode}</div>
                                <div className="columns medium-1 text--right">{fmt(signedAmount(s.action, s.amount))}</div>
                                <div className="columns medium-1 text--right">{fmt(signedAmount(s.action, s.amountExcludingVat))}</div>
                                <div className="columns medium-2">{fmtDate(s.pspSettlementDate)}</div>
                                <div className="columns medium-2">
                                    {result?.pending && <span style={{ color: '#888' }}>Bearbetar...</span>}
                                    {!result?.pending && result?.successMessage && (
                                        <span style={{ color: 'green' }}>{result.successMessage}</span>
                                    )}
                                    {!result?.pending && result?.errorMessage && (
                                        <span style={{ color: 'red' }}>{result.errorMessage}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    <div className="row medium-unstack no-margin simple-table__header">
                        <div className="columns medium-shrink" style={{ visibility: 'hidden' }}>
                            <input type="checkbox" style={{ margin: 0 }} />
                        </div>
                        <div className="columns medium-5">
                            <strong>Totalt ({settlements.length} transaktioner)</strong>
                        </div>
                        <div className="columns medium-1 text--right">
                            <strong>{fmt(totalAmount)}</strong>
                        </div>
                        <div className="columns medium-1 text--right">
                            <strong>{fmt(totalExVat)}</strong>
                        </div>
                        <div className="columns medium-4" />
                    </div>
                </>
            )}
        </div>
    );
};

export default QliroReport;
